import React, { PureComponent } from 'react';
import Select from 'react-select';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import './FundSelector.css';
import PropTypes from 'prop-types';

const colourStyles = {
    control: styles => ({ ...styles, backgroundColor: 'white' }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
        const color = '#abebed';
        return {
            ...styles,
            backgroundColor: isDisabled
                ? null
                : isSelected ? color : isFocused ? color : null,
        };
    },

};

/** @flow */

/**
 * Provides a typeahead interface for entering funds/entities into an app. Gives
 * template download/upload features too.
 * @name FundSelector
 * @example 
 * import React from 'react';
 * import Fundselector from '@bit/wisdomtree.reactcomponents.input.fundselector';
 *
 * export default (
 * 	<Fundselector/>
 * )
 */
class FundSelector extends PureComponent {
   
    constructor(props) { 
        super(props);
        this.handleChange = this.handleChange.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleCapGainsUpload = this.handleCapGainsUpload.bind(this);

        // Store the parameters
        this.params = {
            initialTickers : this.props.initialTickers,
            typeaheadUrl : this.props.typeaheadUrl,
            wtRegion : this.props.wtRegion,
            tickerNamesUrl : this.props.tickerNamesUrl,
            showUpload: this.props.showUpload,
            maxFunds: this.props.maxFunds, 
            token: this.props.token,
        };

        // Handle any initial tickers on load of page
        this.handleInitialTickers();

        // Receive the function to call when the ANALYZE button is pressed
        this.submitFundsToAnalyze = this.props.submitFundsToAnalyze;

        // Initialize local state
        this.state = {
            selectedFunds: null,
            fundsToCompare: [],
            fundSuggestions: [],
            fundSuggestionsForSelect: [],
            counter: 0, // see explanation of requestIndex below
            latestFundSuggestionsRequestIndex: 0
        }

    }

    // Run analysis if tickers are provided to the page
    handleInitialTickers() {
        const {
            initialTickers
        } = this.params;
        // If tickers were passed into the page then load the analysis directly
        if (initialTickers && initialTickers.length > 0) {

            // Check the window's tickers are valid, and if so execute the analysis (uses a promise)
            this.checkCapGainsTickers(initialTickers)
                .then(() => {
                    this.submitFundsToAnalyze(this.state.selectedFunds);
                    this.clearSelectedFunds();
                })
        }
    }

    // Handle changes to the typed input by the User
    handleInputChange = (newValue) => {
        this.typeahead(newValue);
    };

    // Handle changes to selection
    handleChange = (selectedFunds) => {
        // console.log('selectedFunds: ' + selectedFunds);
        this.updateSelectedFunds(selectedFunds);
    };

    // Handle submission of the Funds to analyze
    handleSubmit = () => {
        // console.log('selectedFunds: ' + this.props.fundSelector.selectedFunds);
        this.props.submitFundsToAnalyze(this.state.selectedFunds, this.params);
        this.clearSelectedFunds();
    };

    // Handle upload of a portfolio
    handleCapGainsUpload = (event) => {
        const { target } = event;
        const file = event.target.files[0];
        const capGainsReader = new FileReader();
        let _this = this;

        capGainsReader.onload = (e) => {
            try {
                let binary = "";
                let bytes = new Uint8Array(e.target.result);
                for (var i = 0; i < bytes.byteLength; i++) {
                    binary += String.fromCharCode(bytes[i])
                }
                const wb = XLSX.read(binary, { type: 'binary' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                // const csv = csvParse(XLSX.utils.sheet_to_csv(ws).replace(/\s+,/g, ','));
                const file_contents = Papa.parse(XLSX.utils.sheet_to_csv(ws).replace(/\s+,/g, ','));
                // console.log('XLSX file_contents: ' + file_contents);
                var tickers = file_contents.data.filter(function (ticker) {
                    return ticker[0] !== null && ticker[0].toLowerCase() !== 'tickers' && ticker[0].length > 0;
                }).map(function (ticker) {
                    return (ticker[0])
                })

                // console.log('tickers to process: ' + tickers);
                // Check the tickers provided against the API
                _this.checkCapGainsTickers(tickers)

            } catch (e) {
                // handleUploadError(file.name, function () {
                //     target.click();
                // });
            }
        }
        capGainsReader.onload.bind(this);
        capGainsReader.readAsArrayBuffer(file); 
        target.value = "";

    };

    /// Typeahead method to get the top 15 results from DataSpan API
    async typeahead(query) {

        // requestIndex encodes the order at which queries are sent to the server in order
        // to ensure that a long-running query, whose response arrives after a more recent
        // and relevant query has completed, doesn't override the latest query results
        const requestIndex = this.state.counter;
        this.setState({
            counter: this.state.counter + 1
        });

        if (!query || query.length <= 1) {
            this.load_typeahead_state(requestIndex, query, [])
            return;
        }

        const url = this.params.typeaheadUrl;

        var data = await fetch(url + '?q=' + encodeURIComponent(query) + '&wtRegion=' + this.params.wtRegion + '&excludeCS',
            { 
                headers: {
                    Authorization: this.params.token &&
                        `Token ${this.params.token}`,
                },
            })
            .then(d => d.json())
            .catch(error => {
                console.error(error);
                this.load_typeahead_state(requestIndex, query, [])
            });

        this.load_typeahead_state(requestIndex, query, Array.isArray(data) ? data : []);

    };

    // Update local state with typeahead data
    load_typeahead_state(requestIndex, query, payload) {

        // Reject suggestions that aren't relevant by checking requestIndex, which would indicate
        // whether a more recent set of suggestions was already loaded
        if (requestIndex < this.state.latestFundSuggestionsRequestIndex) {
            return;
        }

        if (payload === undefined || payload.length === 0) {
            return;
        }

        var copy = [];

        // Get the current Funds being analyzed
        var fundsToCompare = this.props.currentFunds;

        payload.forEach((item, index) => {

            // Check if a typeahead result is already being analyzed. Uses externalID
            // as the lookup
            var alreadyInList = fundsToCompare.find(function (externalID) {
                return externalID === item.externalID;
            });

            if (alreadyInList === undefined) {
                copy.push({ 'value': item.externalID, 'label': item.name + " (" + item.ticker + ")" });
            }

        });

        // Update state with updated typeahead results
        this.setState({
            fundSuggestions: payload,
            fundSuggestionsForSelect: copy,
            latestFundSuggestionsRequestIndex: requestIndex,
        })

    }

    // Update the selection bar with the chosen funds from the dropdown
    updateSelectedFunds(selectedFunds) {
        const {
            maxFunds
        } = this.props;
        if (maxFunds > 0 && selectedFunds.length > maxFunds) {
            return;
        }

        this.setState({
            selectedFunds: selectedFunds.length > 0 ? selectedFunds : null
        })

    }

    // Clear the funds in the selection bar
    clearSelectedFunds() {

        this.setState({
            selectedFunds: null,
            fundSuggestions: [],
            fundSuggestionsForSelect: [],
        })

    }

    async checkCapGainsTickers(tickers) {
        const {
            maxFunds
        } = this.params;
        if (maxFunds > 0) {
            tickers = tickers.slice(0, maxFunds);
        }
        const url = this.params.tickerNamesUrl;

        var data = await fetch(url, {
            method: 'POST',
            body: JSON.stringify({ tickers }),
            headers: {
                'Content-Type': 'application/json',
            }
        })
            .then(d => d.json())
            .catch(error => {
                console.error(error);
            });

        let results = [];

        // Check for unknown funds, and only push those that are found
        data.forEach((fund) => {
            if (fund.name !== 'UNKNOWN') {
                results.push(fund);
            }
        });

        if (results.length === 0) {
            return;
        }

        var selected_funds = [];
        var fundstoCompare = this.state.fundsToCompare.slice();

        results.forEach(function (item) {

            // Check if an excel result is already in the FundList
            var alreadyInList = fundstoCompare.find(function (element) {
                return element.ticker === item.ticker;
            });

            if (alreadyInList === undefined) {
                selected_funds.push({ 'value': item.externalID, 'label': item.name + " (" + item.ticker + ")" });
            }

        });

        this.setState({
            selectedFunds: selected_funds.length > 0 ? selected_funds : null
        });

    };

    render() {
        const {
            showUpload
        } = this.params;

        // const {
        //     passedInFunds,
        //     classPassedInFunds,
        // } = this.props;

        // // Get any funds that are passed in
        // const fundsPassedIn = Object.assign([], passedInFunds);
        
        // // If passed In funds has something them erase it
        // if (passedInFunds && fundsPassedIn.length > 0){
        //     classPassedInFunds();
        // }
 
        // const selectedFunds = Array.isArray(fundsPassedIn) && fundsPassedIn.length > 0 ? fundsPassedIn : this.state.selectedFunds;

        return (
            <div className='row fundselector' id='fund-selector'>
                <div className="col-lg-7 col-md-6 col-sm-12">
                    <Select
                        isMulti
                        options={this.state.fundSuggestionsForSelect}
                        onInputChange={this.handleInputChange}
                        onChange={this.handleChange}
                        filterOption={false}
                        placeholder="SEARCH BY TICKER OR NAME"
                        noOptionsMessage={() => null}
                        components={{ IndicatorSeparator: null }}
                        //closeOnSelect={false}
                        closeMenuOnSelect={false}
                        blurInputOnSelect={false}
                        clearable={true}
                        value={this.state.selectedFunds}
                        styles={colourStyles}
                    />
                </div>
                <div className='col-sm-12 visible-xs visible-sm'>&nbsp;</div>
                <div className="col-lg-2 col-md-2">
                    <button disabled={!this.state.selectedFunds} onClick={this.handleSubmit} className={this.state.selectedFunds ? 'enabled' : 'disabled'}>ANALYZE FUNDS</button>
                </div>
                { showUpload &&
                <div className="col-lg-3 col-md-4 hidden-xs">
                    <div className='row'>
                        <div className="upload col-xs-5">
                            <label title='Upload an Excel file with tickers in the first column, or use our template.' htmlFor="upload-cap-gains-excel">
                                Upload Portfolio
                            </label>
                            <input
                                id="upload-cap-gains-excel"
                                type="file"
                                accept=".xls, .xlsx, .csv"
                                onChange={this.handleCapGainsUpload} />
                        </div>
                        <div className="template col-xs-6">
                            <a title='Download an example Excel template you can update with your tickers.' href='https://www.wisdomtree.com/-/media/us-media-files/advisor-solutions/digital-portfolio-developer/xls/capgainstemplate.xlsx' _target='blank'>Download Template</a>
                        </div>
                    </div>
                </div>
                }
            </div>
        );
    }

}

FundSelector.propTypes = {
    
    /**
     * Function that the ANALYZE button will call when pushed
     */
    submitFundsToAnalyze: PropTypes.func.isRequired,
    /**
     * List of initial tickers to be loaded on start
     */
    initialTickers: PropTypes.array,

    /**
     * List of tickers that can be loaded from history
     */
    passedInFunds: PropTypes.array,
    
    /**
     * Function to remove the passed in funds when this component is done with them
     */
    classPassedInFunds: PropTypes.func,

    /**
     * URL of the typeahead API endpoint. This is typically
     * DataSpanAPI 
     */
    typeaheadUrl: PropTypes.string.isRequired,
    /**
     * URL of the ticker name checking API endpoint. This will check a list
     * from an Excel file and validate that the tickers are valid. This is typically
     * DataSpanAPI 
     */
    tickerNamesUrl: PropTypes.string.isRequired,
    /**
     * The region to load the typeahead for (e.g., US)
     */
    wtRegion: PropTypes.string.isRequired,
    /**
     * The list of funds kept by the calling component so the FundSelector can remove
     * duplicates
     */
    currentFunds: PropTypes.array,
    /**
     * Whether to show the upload / template or not, defaults to true
     */
    showUpload: PropTypes.bool,
    /**
     * Maximum number of funds you can select
     */
    maxFunds: PropTypes.number,
};

FundSelector.defaultProps = {
    submitFundsToAnalyze: (selectedFunds) => {},
    initialTickers: [],
    typeaheadUrl: 'https://dataspanapi.wisdomtree.com/capgains/typeahead/',
    tickerNamesUrl: 'https://dataspanapi.wisdomtree.com/capgains/ticker_names/',
    wtRegion: 'US',
    currentFunds: [],
    showUpload: true,
    maxFunds: -1,
}

export default (FundSelector);


// WEBPACK FOOTER //
// ./src/components/FundSelector/FundSelector.js